import { useMemo, useState } from "react";
import { Trash2, Search, HardDrive } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Btn } from "../../components/Btn";
import { useToast } from "../../components/Toast";
import { useApiFallback } from "../../hooks/useApiFallback";
import { getLocalCache, purgeLocalCache } from "../../api/endpoints";
import { ApiError } from "../../api/types";
import type { LocalCachedPackage } from "../../api/types";
import { applications, NODE_INFO } from "../../data";

// Fallback — берём первые 12 приложений из mock-каталога и переводим в форму
// /local/cache (pkg, name, version, size_mb, generation).
const FALLBACK_CACHE: LocalCachedPackage[] = applications.slice(0, 12).map((a) => ({
  pkg: a.pkg,
  name: a.name,
  version: a.version,
  size_mb: a.sizeMb,
  generation: NODE_INFO.generation,
}));

export default function CachePage() {
  const push = useToast();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [pendingPkg, setPendingPkg] = useState<string | null>(null);

  const cacheQ = useQuery({
    queryKey: ["local", "cache"],
    queryFn: getLocalCache,
  });

  const cache = useApiFallback<LocalCachedPackage[]>(
    cacheQ.data,
    cacheQ.isError,
    FALLBACK_CACHE,
  );

  const purgeMut = useMutation({
    mutationFn: (pkg: string) => purgeLocalCache(pkg),
    onMutate: (pkg) => setPendingPkg(pkg),
    onSuccess: async (res) => {
      await qc.invalidateQueries({ queryKey: ["local", "cache"] });
      push({ kind: "success", text: `Кэш ${res.pkg} очищен` });
    },
    onError: (e) => {
      const msg = e instanceof ApiError ? e.message : "Не удалось очистить кэш";
      push({ kind: "warn", text: msg });
    },
    onSettled: () => setPendingPkg(null),
  });
  const purgeAllMut = useMutation({
    mutationFn: async () => {
      const targets = cache.value.map((item) => item.pkg);
      // Кэш хранится одним серверным списком. Параллельные DELETE-запросы
      // конкурируют за его обновление и могут перетереть результат друг друга
      // (а на SQLite — ещё и столкнуться с блокировкой). Выполняем массовую
      // очистку последовательно, чтобы каждая операция видела свежий список.
      for (const pkg of targets) {
        await purgeLocalCache(pkg);
      }
      return targets.length;
    },
    onSuccess: async (count) => {
      await qc.invalidateQueries({ queryKey: ["local", "cache"] });
      push({ kind: "success", text: `Кэш очищен, удалено пакетов: ${count}` });
    },
    onError: (error) => push({
      kind: "warn",
      text: error instanceof ApiError ? error.message : "Не удалось полностью очистить кэш",
    }),
  });

  const items = cache.value;

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const t = q.toLowerCase();
    return items.filter((c) => c.pkg.toLowerCase().includes(t) || c.name.toLowerCase().includes(t));
  }, [q, items]);

  const totalGB = useMemo(
    () => items.reduce((s, c) => s + c.size_mb, 0) / 1024,
    [items],
  );

  return (
    <>
      <header className="flex flex-col gap-3 pb-5 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <h1 className="text-[20px] font-semibold leading-tight tracking-[-0.01em] text-[var(--color-fg)] sm:text-[22px]">
            Кэш приложений
          </h1>
          <div className="mt-1 text-[12.5px] text-[var(--color-fg-muted)]">
            {items.length} приложений · {totalGB.toFixed(2)} ГБ в кэше локала
            {cacheQ.isFetching && " · обновляется…"}
          </div>
        </div>
        <Btn
          icon={Trash2}
          disabled={!items.length || purgeAllMut.isPending}
          onClick={() => {
            if (window.confirm(`Удалить из кэша все пакеты (${items.length})?`)) purgeAllMut.mutate();
          }}
        >
          {purgeAllMut.isPending ? "Очистка…" : "Очистить весь кэш"}
        </Btn>
      </header>

      <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
        <div className="flex items-center gap-3 text-[12.5px]">
          <HardDrive size={16} className="text-[var(--color-brand-2)]" />
          <span className="text-[var(--color-fg-muted)]">Использовано локалом</span>
          <span className="font-mono font-medium text-[var(--color-fg)]">
            {totalGB.toFixed(2)} ГБ
          </span>
        </div>
        <div className="mt-3 h-2 overflow-hidden rounded-full bg-[var(--color-surface-3)]">
          <div
            className="h-full rounded-full bg-[var(--color-brand-2)]"
            style={{ width: `${Math.min(100, (totalGB / NODE_INFO.diskCapGB) * 100)}%` }}
          />
        </div>
        <div className="mt-2 flex justify-between text-[11px] text-[var(--color-fg-muted)]">
          <span>пакетов: {items.length}</span>
          <span>лимит диска: {NODE_INFO.diskCapGB} ГБ</span>
        </div>
      </section>

      <div className="relative mt-3">
        <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-fg-faint)]" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Поиск приложения"
          className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] py-2.5 pl-9 pr-3 text-[13px] text-[var(--color-fg)] outline-none placeholder:text-[var(--color-fg-faint)] focus:border-[var(--color-fg-muted)]"
        />
      </div>

      <section className="mt-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] overflow-hidden">
        <div className="-mx-1 overflow-x-auto">
          <table className="w-full min-w-[640px] text-[12.5px]">
            <thead>
              <tr className="bg-[var(--color-surface-2)] text-left text-[10.5px] uppercase tracking-[0.08em] text-[var(--color-fg-muted)]">
                <Th>Приложение</Th>
                <Th>Версия</Th>
                <Th>Поколение</Th>
                <Th>Размер</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => (
                <tr
                  key={c.pkg}
                  style={{ animationDelay: `${Math.min(i, 8) * 25}ms` }}
                  className="stagger-up border-t border-[var(--color-border)]"
                >
                  <td className="whitespace-nowrap px-3 py-2.5">
                    <div className="flex items-center gap-2.5">
                      <span
                        className="grid h-8 w-8 shrink-0 place-items-center rounded-lg font-semibold text-white"
                        style={{ background: pickColor(c.pkg), fontSize: 13 }}
                      >
                        {c.name.slice(0, 1).toUpperCase()}
                      </span>
                      <div>
                        <div className="font-medium">{c.name}</div>
                        <div className="font-mono text-[11px] text-[var(--color-fg-muted)]">{c.pkg}</div>
                      </div>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-3 py-2.5 font-mono">{c.version}</td>
                  <td className="whitespace-nowrap px-3 py-2.5 font-mono text-[var(--color-fg-muted)]">{c.generation}</td>
                  <td className="whitespace-nowrap px-3 py-2.5 font-mono">{c.size_mb.toFixed(1)} MB</td>
                  <td className="whitespace-nowrap px-3 py-2.5">
                    <button
                      onClick={() => purgeMut.mutate(c.pkg)}
                      disabled={pendingPkg === c.pkg}
                      className="text-[12px] text-[var(--color-fg-muted)] transition-colors hover:text-[var(--color-danger)] disabled:opacity-60"
                    >
                      {pendingPkg === c.pkg ? "очистка…" : "очистить"}
                    </button>
                  </td>
                </tr>
              ))}
              {!filtered.length && (
                <tr><td colSpan={5} className="px-3 py-8 text-center text-[12.5px] text-[var(--color-fg-muted)]">
                  {q.trim()
                    ? `По запросу «${q}» ничего не найдено.`
                    : "Кэш пуст."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

// Маленькая помощь: подбираем «иконке» цвет по стабильному хэшу pkg.
const PALETTE = ["#1f3a8a", "#0ea5e9", "#0891b2", "#15803d", "#a16207", "#b91c1c", "#7c3aed", "#c2410c"];
function pickColor(pkg: string): string {
  let h = 0;
  for (let i = 0; i < pkg.length; i++) h = (h * 31 + pkg.charCodeAt(i)) | 0;
  return PALETTE[Math.abs(h) % PALETTE.length];
}

function Th({ children }: { children?: React.ReactNode }) {
  return <th className="px-3 py-2 font-medium">{children}</th>;
}
